/**
 * DESIGN_SYSTEM.md §8.8: "the theme class/attribute is set from the localStorage mirror
 * by a small blocking step before first paint... No flash of the wrong theme."
 *
 * This function's SOURCE (not a bundled reference to it) is meant to be inlined into
 * the internal app's `index.html` as a synchronous `<script>` tag, before the app's own
 * bundle loads — that is the only way to actually run before first paint. Import
 * `themeInitScriptSource` and inline it yourself, e.g.:
 *
 *   <script>${themeInitScriptSource}</script>
 *
 * NEVER call `applyStoredTheme()` from inside a React effect and call that "no flash" —
 * an effect runs after the first paint by definition.
 *
 * Internal portal only (§8.1) — the public app must never inline this script or import
 * from `@ministry/ui/internal`.
 */
export const THEME_STORAGE_KEY = 'ministry-ui-theme';
function resolveTheme(preference) {
    if (preference === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return preference;
}
/** Runs in the browser only; called by the inlined script and by ThemeProvider's hydration check. */
export function applyStoredTheme() {
    let preference = 'system';
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'system')
            preference = stored;
    }
    catch {
        // localStorage unavailable (private mode, disabled storage) — fall back to system.
    }
    const resolved = resolveTheme(preference);
    document.documentElement.setAttribute('data-theme', resolved);
    return resolved;
}
/**
 * The literal source string to inline in a `<script>` tag in the internal app shell's
 * `index.html`, ahead of the app bundle. Kept as a standalone string (not
 * `applyStoredTheme.toString()`) so it has zero dependency on how this module gets
 * bundled/minified — it must survive being pasted into raw HTML verbatim.
 */
export const themeInitScriptSource = `(function(){try{var k=${JSON.stringify(THEME_STORAGE_KEY)};var v=localStorage.getItem(k)||'system';var d=v==='system'?(matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light'):v;document.documentElement.setAttribute('data-theme',d);}catch(e){document.documentElement.setAttribute('data-theme','light');}})();`;
//# sourceMappingURL=theme-script.js.map